import React from 'react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { SearchX, TriangleAlert } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { ELLIPSIS, paginationRange } from '@utils/paginationRange';
import { SEARCH } from '@constants/search';
import type { SearchResult } from '../../types';
import SearchResultCard from './SearchResultCard';

interface SearchResultsProps {
  results: SearchResult[];
  isLoading: boolean;
  error: string | null;
  onRetry?: () => void;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onResultClick: (result: SearchResult) => void;
  searchTime?: number;
  query: string;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  isLoading,
  error,
  onRetry,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onResultClick,
  searchTime,
  query,
}) => {
  const { t } = useTranslation();
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: 4 }, (_, index) => (
          <Card key={index} className="flex gap-3 p-4">
            <Skeleton className="size-9 shrink-0 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <TriangleAlert className="size-4" strokeWidth={2} />
        <AlertTitle>{t('search.searchError')}</AlertTitle>
        <AlertDescription className="revy-cjk">{error}</AlertDescription>
        {onRetry && (
          <Button variant="outline" size="sm" className="mt-2 w-fit" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        )}
      </Alert>
    );
  }

  if (results.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <span className="grid size-12 place-items-center rounded-2xl bg-muted text-muted-foreground">
          <SearchX className="size-6" strokeWidth={1.6} />
        </span>
        <p className="revy-cjk text-base font-medium leading-[1.5]">{t('search.noResults')}</p>
        <p className="revy-cjk max-w-sm text-sm leading-[1.6] text-muted-foreground">
          {t('search.noDocumentsMatch', { query })}
        </p>
        <p className="revy-cjk max-w-sm text-xs leading-[1.6] text-muted-foreground">
          {t('search.noResultsHint')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="revy-cjk text-sm text-muted-foreground">
          {query
            ? t('search.resultsSummaryForQuery', { total: totalCount.toLocaleString(), query })
            : t('search.resultsSummary', { total: totalCount.toLocaleString() })}
          {searchTime !== undefined &&
            ` ${t('search.searchTime', { time: searchTime.toFixed(2) })}`}
        </p>

        <Select value={String(pageSize)} onValueChange={(value) => onPageSizeChange(Number(value))}>
          <SelectTrigger aria-label={t('table.rowsPerPage')} className="h-8 w-[4.5rem] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="revy-ui">
            {SEARCH.PAGE_SIZES.map((size) => (
              <SelectItem key={size} value={String(size)} className="revy-figure text-xs">
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-3">
        {results.map((result) => (
          <SearchResultCard key={result.id} result={result} onSelect={onResultClick} />
        ))}
      </div>

      {pageCount > 1 && (
        <Pagination className="pt-2">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                aria-label={t('table.previousPage')}
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
              />
            </PaginationItem>

            {paginationRange(page, pageCount).map((slot, index) =>
              slot === ELLIPSIS ? (
                <PaginationItem key={`gap-${index}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={slot}>
                  <PaginationLink
                    size="icon-sm"
                    isActive={slot === page}
                    aria-label={t('table.goToPage', { page: slot })}
                    onClick={() => onPageChange(slot)}
                    className="revy-figure"
                  >
                    {slot}
                  </PaginationLink>
                </PaginationItem>
              ),
            )}

            <PaginationItem>
              <PaginationNext
                aria-label={t('table.nextPage')}
                disabled={page >= pageCount}
                onClick={() => onPageChange(page + 1)}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
};

export default SearchResults;
